import getEndpoint from "./getEndpoint.js";
import { generateVisitorId } from "./analytics.js";
import { getReduxValue } from "./redux.js";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const RELEASE =
  import.meta.env.VITE_APP_RELEASE ||
  (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev");
const ENVIRONMENT = import.meta.env.MODE;
const CAPTURE_DEDUPE_MS = 30000;
const CAPTURE_WINDOW_MS = 5 * 60 * 1000;
const MAX_QUEUE_SIZE = 50;
const QUEUE_STORAGE_KEY = "viz_error_queue_v1";
const MAX_BREADCRUMBS = 20;
const SENSITIVE_KEY_REGEX =
  /(pass(word)?|token|secret|authorization|cookie|api[-_]?key|credit|card|cvv|ssn|jwt|email|phone)/i;
const recentErrors = new Map();
const burstControl = new Map();
const breadcrumbsStore = [];
let listenersInstalled = false;
let lastCapturedRequestId = "";

const trim = (value, maxLength) => {
  if (typeof value !== "string") return value;
  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

const safeNowIso = () => new Date().toISOString();

const readQueue = () => {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (queue) => {
  try {
    localStorage.setItem(
      QUEUE_STORAGE_KEY,
      JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)),
    );
  } catch {
    // noop
  }
};

const enqueuePayload = (payload) => {
  const retryCount = (payload._retryCount || 0) + 1;
  if (retryCount > 3) return; // ← drop after 3 failed attempts, never re-enqueue
  const queue = readQueue();
  queue.push({ ...payload, _retryCount: retryCount });
  writeQueue(queue);
};

const popQueuedPayloads = () => {
  const queue = readQueue();
  writeQueue([]);
  return queue;
};

const sanitizeValue = (value, depth = 0) => {
  if (depth > 5) return "[Truncated]";

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  ) {
    return value;
  }

  if (typeof value === "string") {
    return trim(value, 1000);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 30).map((entry) => sanitizeValue(entry, depth + 1));
  }

  if (typeof value === "object") {
    const output = {};
    for (const [key, val] of Object.entries(value).slice(0, 40)) {
      if (SENSITIVE_KEY_REGEX.test(key)) {
        output[key] = "[REDACTED]";
      } else {
        output[key] = sanitizeValue(val, depth + 1);
      }
    }
    return output;
  }

  return String(value);
};

const stringifySafe = (value) => {
  const seen = new WeakSet();
  return JSON.stringify(value, (key, val) => {
    if (typeof val === "object" && val !== null) {
      if (seen.has(val)) return "[Circular]";
      seen.add(val);
    }
    if (typeof val === "string" && val.length > 1000) {
      return `${val.slice(0, 1000)}...[truncated]`;
    }
    return val;
  });
};

const buildErrorKey = ({ message, name, stack, route }) =>
  `${name || ""}|${message || ""}|${stack || ""}|${route || ""}`;

const shouldCapture = (errorKey) => {
  const now = Date.now();

  const previous = recentErrors.get(errorKey);
  if (previous && now - previous < CAPTURE_DEDUPE_MS) {
    return false;
  }
  recentErrors.set(errorKey, now);

  const burst = burstControl.get(errorKey) || { count: 0, startAt: now };
  if (now - burst.startAt > CAPTURE_WINDOW_MS) {
    burst.count = 0;
    burst.startAt = now;
  }
  burst.count += 1;
  burstControl.set(errorKey, burst);

  if (burst.count > 20 && burst.count % 10 !== 0) {
    return false;
  }
  if (burst.count > 8 && burst.count % 3 !== 0) {
    return false;
  }

  if (recentErrors.size > 300) {
    for (const [key, value] of recentErrors) {
      if (now - value > CAPTURE_DEDUPE_MS) {
        recentErrors.delete(key);
      }
    }
  }
  return true;
};

const getBasePayload = () => ({
  source: "frontend",
  level: "error",
  route: window.location?.pathname || "",
  url: window.location?.href || "",
  release: RELEASE,
  environment: ENVIRONMENT,
  visitorId: generateVisitorId(),
});

export const addBreadcrumb = (label, metadata = {}) => {
  if (!label || typeof label !== "string") return;
  const entry = {
    label: trim(label, 120),
    at: safeNowIso(),
    route: window.location?.pathname || "",
    metadata: sanitizeValue(metadata),
  };
  breadcrumbsStore.push(entry);
  if (breadcrumbsStore.length > MAX_BREADCRUMBS) {
    breadcrumbsStore.splice(0, breadcrumbsStore.length - MAX_BREADCRUMBS);
  }
};

const getBreadcrumbLabels = () => {
  return breadcrumbsStore.slice(-MAX_BREADCRUMBS).map((item) => {
    const serializedMeta = Object.keys(item.metadata || {}).length
      ? ` ${JSON.stringify(item.metadata)}`
      : "";
    return `${item.at} | ${item.label}${serializedMeta}`;
  });
};

export const getLastCapturedRequestId = () => lastCapturedRequestId;

export const setLastCapturedRequestId = (requestId) => {
  if (!requestId || typeof requestId !== "string") return;
  lastCapturedRequestId = trim(requestId, 120);
};

const postPayload = async (payload) => {
  const endpoint = getEndpoint("errors", "postErrorEvent");
  const url = BASE_URL + endpoint.path;
  const accessToken = getReduxValue("auth.accessToken");
  const body = stringifySafe(payload);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body,
    keepalive: true,
  });

  return response.ok;
};

const sendErrorPayload = (payload) => {
  try {
    if (!navigator.onLine) {
      enqueuePayload(payload);
      return;
    }

    postPayload(payload)
      .then((ok) => {
        if (!ok) enqueuePayload(payload);
      })
      .catch(() => {
        enqueuePayload(payload);
      });
  } catch {
    // Never throw from reporter.
  }
};

export const reportFrontendError = ({
  error,
  message,
  name,
  stack,
  metadata = {},
  breadcrumbs = [],
}) => {
  const resolvedMessage = trim(
    message || error?.message || "Unexpected frontend error",
    1000,
  );
  const resolvedName = trim(name || error?.name || "Error", 120);
  const resolvedStack = trim(stack || error?.stack || "", 15000);
  const route = window.location?.pathname || "";
  const errorKey = buildErrorKey({
    message: resolvedMessage,
    name: resolvedName,
    stack: resolvedStack,
    route,
  });

  if (!shouldCapture(errorKey)) return;

  const sanitizedMetadata = sanitizeValue(metadata || {});
  if (lastCapturedRequestId && !sanitizedMetadata.requestId) {
    sanitizedMetadata.requestId = lastCapturedRequestId;
  }

  sendErrorPayload({
    ...getBasePayload(),
    message: resolvedMessage,
    name: resolvedName,
    stack: resolvedStack,
    metadata: sanitizedMetadata,
    breadcrumbs:
      Array.isArray(breadcrumbs) && breadcrumbs.length
        ? breadcrumbs.slice(0, MAX_BREADCRUMBS)
        : getBreadcrumbLabels(),
  });
};

export const reportApiFailure = ({
  statusCode,
  message,
  endpointPath,
  method,
  requestId,
  responseBody,
  query,
}) => {
  const isNetworkFailure = !statusCode || statusCode <= 0;
  const isServerFailure = statusCode >= 500;
  if (!isNetworkFailure && !isServerFailure) return;

  setLastCapturedRequestId(requestId);
  addBreadcrumb("api_failure", {
    endpointPath,
    method,
    statusCode,
  });

  reportFrontendError({
    name: "ApiRequestFailure",
    message: message || "API request failed unexpectedly",
    metadata: {
      type: "api_failure",
      statusCode,
      endpointPath,
      method,
      requestId,
      query: sanitizeValue(query || {}),
      responseBody: sanitizeValue(responseBody || {}),
    },
  });
};

const patchHistoryForBreadcrumbs = () => {
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function (...args) {
    originalPushState.apply(window.history, args);
    addBreadcrumb("route_change", {
      type: "pushState",
      to: window.location.pathname,
    });
  };

  window.history.replaceState = function (...args) {
    originalReplaceState.apply(window.history, args);
    addBreadcrumb("route_change", {
      type: "replaceState",
      to: window.location.pathname,
    });
  };
};

const installInteractionBreadcrumbs = () => {
  window.addEventListener("click", (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;
    const clickable = target.closest("button, a, [role='button']");
    if (!clickable) return;
    const text = trim(
      clickable.getAttribute("data-error-breadcrumb") ||
        clickable.getAttribute("aria-label") ||
        clickable.textContent ||
        clickable.tagName,
      80,
    );
    addBreadcrumb("click", {
      target: text,
      tag: clickable.tagName,
    });
  });
};

const flushQueuedPayloads = () => {
  const queued = popQueuedPayloads();
  if (!queued.length) return;
  const batch = queued.slice(-MAX_QUEUE_SIZE);
  Promise.allSettled(batch.map((payload) => postPayload(payload))).then(
    (results) => {
      const failed = [];
      results.forEach((result, index) => {
        if (result.status !== "fulfilled" || result.value !== true) {
          failed.push(batch[index]);
        }
      });
      if (failed.length) {
        failed.forEach((payload) => enqueuePayload(payload));
      }
    },
  );
};

export const initGlobalErrorCapture = () => {
  if (listenersInstalled) return;
  listenersInstalled = true;
  addBreadcrumb("app_boot", { environment: ENVIRONMENT, release: RELEASE });
  patchHistoryForBreadcrumbs();
  installInteractionBreadcrumbs();
  window.addEventListener("online", flushQueuedPayloads);
  window.addEventListener("popstate", () => {
    addBreadcrumb("route_change", {
      type: "popstate",
      to: window.location.pathname,
    });
  });
  flushQueuedPayloads();

  window.addEventListener("error", (event) => {
    reportFrontendError({
      error: event.error,
      message: event.message,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (reason instanceof Error) {
      reportFrontendError({
        error: reason,
        metadata: { type: "unhandledrejection" },
      });
      return;
    }
    reportFrontendError({
      message: "Unhandled promise rejection",
      name: "UnhandledRejection",
      metadata: {
        type: "unhandledrejection",
        reason: sanitizeValue(reason),
      },
    });
  });
};
